import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import UserGrowthModal from './UserGrowthModal';
import ActivityChartModal from './ActivityChartModal';
import { dashboardAPI, usersAPI } from '../../services/api';
import './Overview.css';

const Overview = () => {
  const { t } = useTranslation('dashboard');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Real data state (all values from GET /dashboard/stats — no mock data)
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newSignups: 0,
    premiumUsers: 0,
    teachers: 0,
    students: 0,
    totalArticles: 0,
    totalCourses: 0,
    totalLiveClasses: 0,
    totalEarnings: 0,
    recentActivity: []
  });

  const [analyticsData, setAnalyticsData] = useState({
    todayActive: 0,
    weekActive: 0,
    monthActive: 0,
    avgSession: 0,
    userGrowth: []
  });

  const [statisticsData, setStatisticsData] = useState({
    growthRate: 0,
    retentionRate: 0,
    performance: 0,
    errorRate: 0,
    satisfaction: 0
  });

  const [activityData, setActivityData] = useState({
    activeSessions: 0,
    pageViews: 0,
    bounceRate: 0,
    conversionRate: 0
  });

  const [monitoringData, setMonitoringData] = useState({
    cpuUsage: 0,
    memoryUsage: 0,
    networkStatus: 'Good',
    databaseStatus: 'Healthy',
    securityStatus: 'Protected',
    uptime: '99.9%'
  });

  // Activity chart state
  const [activityChartData, setActivityChartData] = useState({
    daily: {
      labels: [],
      totalActivities: [],
      uniqueUsers: []
    },
    hourly: {
      labels: [],
      data: []
    },
    metrics: {
      totalActivitiesThisWeek: 0,
      avgDailyActivities: 0,
      peakActivityDay: 'No activity',
      activityGrowth: 0,
      activityBreakdown: []
    }
  });

  // Reports state
  const [reportsData, setReportsData] = useState({
    monthlyReport: { status: 'ready', lastGenerated: null, dataPoints: 0 },
    analyticsReport: { status: 'processing', lastGenerated: null, dataPoints: 0 },
    financialReport: { status: 'ready', lastGenerated: null, dataPoints: 0 },
    securityReport: { status: 'pending', lastGenerated: null, dataPoints: 0 },
    educationReport: { status: 'ready', lastGenerated: null, dataPoints: 0 },
    userReport: { status: 'ready', lastGenerated: null, dataPoints: 0 }
  });

  const [generatingReport, setGeneratingReport] = useState(false);

  // Modal states
  const [isUserGrowthModalOpen, setIsUserGrowthModalOpen] = useState(false);
  const [isActivityChartModalOpen, setIsActivityChartModalOpen] = useState(false);
  const [lastMonitoringUpdate, setLastMonitoringUpdate] = useState(null);
  const [showActiveUsersModal, setShowActiveUsersModal] = useState(false);
  const [activeUsersList, setActiveUsersList] = useState([]);
  const [loadingActiveUsers, setLoadingActiveUsers] = useState(false);
  const [activeUsersWindowMinutes, setActiveUsersWindowMinutes] = useState(15);
  const [activeUsersModalMode, setActiveUsersModalMode] = useState('current'); // 'current' | 'today' | 'window'
  const [activeUsersWindowDays, setActiveUsersWindowDays] = useState(7);
  const activeUsersCardRef = useRef(null);

  // User creation state
  const [showUserModal, setShowUserModal] = useState(false);
  
  // Debug modal state
  useEffect(() => {
    console.log('Modal state changed:', showUserModal);
  }, [showUserModal]);
  const [userForm, setUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    role: 'student'
  });
  const [userFormErrors, setUserFormErrors] = useState({});

  // Search filter functions
  const filterCards = (cards, searchTerm) => {
    if (!searchTerm.trim()) return cards;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return cards.filter(card => 
      card.title.toLowerCase().includes(lowerSearchTerm) ||
      card.description.toLowerCase().includes(lowerSearchTerm) ||
      card.value.toString().toLowerCase().includes(lowerSearchTerm)
    );
  };

  const filterActivities = (activities, searchTerm) => {
    if (!searchTerm.trim()) return activities;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return activities.filter(activity => 
      activity.action.toLowerCase().includes(lowerSearchTerm) ||
      activity.item.toLowerCase().includes(lowerSearchTerm) ||
      activity.type.toLowerCase().includes(lowerSearchTerm)
    );
  };

  const tabs = [
    { id: 'dashboard', label: t('tabs.dashboard'), icon: 'dashboard' },
    { id: 'analytics', label: t('tabs.analytics'), icon: 'analytics' },
    { id: 'statistics', label: t('tabs.statistics'), icon: 'bar_chart' },
    { id: 'activity', label: t('tabs.activity'), icon: 'trending_up' },
    { id: 'reports', label: t('tabs.reports'), icon: 'assessment' },
    { id: 'actions', label: t('tabs.actions'), icon: 'touch_app' },
    { id: 'monitoring', label: t('tabs.monitoring'), icon: 'monitor' },
    { id: 'insights', label: t('tabs.insights'), icon: 'lightbulb' }
  ];

  const fetchActiveUsersList = async () => {
    try {
      setLoadingActiveUsers(true);
      setError(null);
      const data = await dashboardAPI.getActiveUsers(200);
      if (!data.success) {
        throw new Error(data?.error || data?.message || 'Failed to fetch active users');
      }

      const list = Array.isArray(data.users) ? data.users : [];
      setActiveUsersList(list);
      setActiveUsersWindowMinutes(Number(data.activeWindowMinutes) || 15);
      // Keep the dashboard card in sync with the real list we just fetched
      setDashboardStats((prev) => ({
        ...prev,
        activeUsers: list.length
      }));
    } catch (err) {
      console.error('Error fetching active users list:', err);
      setActiveUsersList([]);
      setError(err.message);
    } finally {
      setLoadingActiveUsers(false);
    }
  };

  const fetchActiveUsersTodayList = async () => {
    try {
      setLoadingActiveUsers(true);
      setError(null);
      const data = await dashboardAPI.getActiveUsersToday(2000);
      if (!data.success) {
        throw new Error(data?.error || data?.message || 'Failed to fetch active users today');
      }

      const list = Array.isArray(data.users) ? data.users : [];
      setActiveUsersList(list);
      setActiveUsersWindowMinutes(0);
    } catch (err) {
      console.error('Error fetching active users today list:', err);
      setActiveUsersList([]);
      setError(err.message);
    } finally {
      setLoadingActiveUsers(false);
    }
  };

  const fetchActiveUsersWindowList = async (days = 7) => {
    try {
      const d = Number(days) || 7;
      setLoadingActiveUsers(true);
      setError(null);
      const data = await dashboardAPI.getActiveUsersWindow({ days: d, limit: 5000 });
      if (!data.success) {
        throw new Error(data?.error || data?.message || 'Failed to fetch active users window');
      }

      const list = Array.isArray(data.users) ? data.users : [];
      setActiveUsersList(list);
      setActiveUsersWindowMinutes(0);
      setActiveUsersWindowDays(Number(data.days) || d);
    } catch (err) {
      console.error('Error fetching active users window list:', err);
      setActiveUsersList([]);
      setError(err.message);
    } finally {
      setLoadingActiveUsers(false);
    }
  };

  const handleOpenActiveUsers = async () => {
    setActiveUsersModalMode('current');
    setShowActiveUsersModal(true);
    await fetchActiveUsersList();
  };

  const handleOpenActiveUsersToday = async () => {
    setActiveUsersModalMode('today');
    setShowActiveUsersModal(true);
    await fetchActiveUsersTodayList();
  };

  const handleOpenWeeklyActiveUsers = async () => {
    setActiveUsersModalMode('window');
    setActiveUsersWindowDays(7);
    setShowActiveUsersModal(true);
    await fetchActiveUsersWindowList(7);
  };

  const handleOpenMonthlyActiveUsers = async () => {
    setActiveUsersModalMode('window');
    setActiveUsersWindowDays(30);
    setShowActiveUsersModal(true);
    await fetchActiveUsersWindowList(30);
  };

  // Allow other areas (AnalyticsReporting) to deep-link/focus to Active Users
  useEffect(() => {
    let focus = null;
    try {
      focus = sessionStorage.getItem('overview_focus');
    } catch (_) {
      focus = null;
    }
    if (focus !== 'active_users' && focus !== 'active_users_today') return;

    // one-shot
    try {
      sessionStorage.removeItem('overview_focus');
    } catch (_) {
      // ignore
    }

    // Ensure we are on the dashboard tab (where the cards live)
    setActiveTab('dashboard');

    // Scroll + open modal after next paint
    setTimeout(() => {
      const el = activeUsersCardRef.current;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      if (focus === 'active_users_today') {
        handleOpenActiveUsersToday();
      } else {
        handleOpenActiveUsers();
      }
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // API Functions
  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await dashboardAPI.getStats();
      // Use only API response — ensure all stat keys exist (real DB counts)
      setDashboardStats({
        totalUsers: Number(data.totalUsers) || 0,
        activeUsers: Number(data.activeUsers) || 0,
        newSignups: Number(data.newSignups) || 0,
        premiumUsers: Number(data.premiumUsers) || 0,
        teachers: Number(data.teachers) || 0,
        students: Number(data.students) || 0,
        totalArticles: Number(data.totalArticles) || 0,
        totalCourses: Number(data.totalCourses) || 0,
        totalLiveClasses: Number(data.totalLiveClasses) || 0,
        totalEarnings: Number(data.totalEarnings) || 0,
        recentActivity: Array.isArray(data.recentActivity) ? data.recentActivity : []
      });
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError(err.message);
      // No fallback mock data - only show real data
      setDashboardStats({
        totalUsers: 0,
        activeUsers: 0,
        newSignups: 0,
        premiumUsers: 0,
        teachers: 0,
        students: 0,
        totalArticles: 0,
        totalCourses: 0,
        totalLiveClasses: 0,
        totalEarnings: 0,
        recentActivity: []
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await dashboardAPI.getAnalytics();
      // Nested payload + flat keys for Overview analytics cards (flat wins)
      setAnalyticsData({
        ...data,
        todayActive: Number(data.todayActive ?? data?.overview?.totalActiveUsers?.today) || 0,
        weekActive: Number(data.weekActive ?? data?.overview?.totalActiveUsers?.week) || 0,
        monthActive: Number(data.monthActive ?? data?.overview?.totalActiveUsers?.month) || 0,
        avgSession: Number(data.avgSession ?? data?.usersRetention?.averageSessionTime) || 0,
        userGrowth: Array.isArray(data.userGrowth) ? data.userGrowth : []
      });
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message);
      // Fallback to real zero values
      setAnalyticsData({
        todayActive: 0,
        weekActive: 0,
        monthActive: 0,
        avgSession: 0,
        userGrowth: []
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStatisticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await dashboardAPI.getStatistics();
      setStatisticsData(data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError(err.message);
      // Fallback to real zero values
      setStatisticsData({
        growthRate: 0,
        retentionRate: 0,
        performance: 0,
        errorRate: 0,
        satisfaction: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await dashboardAPI.getActivity();
      setActivityData(data);
    } catch (err) {
      console.error('Error fetching activity:', err);
      setError(err.message);
      // Fallback to real zero values
      setActivityData({
        activeSessions: 0,
        pageViews: 0,
        bounceRate: 0,
        conversionRate: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMonitoringData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await dashboardAPI.getMonitoring();
      setMonitoringData(data);
      setLastMonitoringUpdate(new Date());
    } catch (err) {
      console.error('Error fetching monitoring:', err);
      setError(err.message);
      // Fallback to real zero values
      setMonitoringData({
        cpuUsage: 0,
        memoryUsage: 0,
        networkStatus: 'Unknown',
        databaseStatus: 'Unknown',
        securityStatus: 'Unknown',
        uptime: '0%',
        processUptime: '0h',
        loadAverage: [0, 0, 0],
        totalMemory: 0,
        freeMemory: 0,
        platform: 'unknown',
        nodeVersion: process.version,
        pid: process.pid
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityChartData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await dashboardAPI.getActivityChart();
      setActivityChartData(data);
    } catch (err) {
      console.error('Error fetching activity chart:', err);
      setError(err.message);
      // Fallback to real zero values
      setActivityChartData({
        daily: {
          labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
          totalActivities: [0, 0, 0, 0, 0, 0, 0],
          uniqueUsers: [0, 0, 0, 0, 0, 0, 0]
        },
        hourly: {
          labels: Array.from({length: 24}, (_, i) => `${i}:00`),
          data: Array.from({length: 24}, () => 0)
        },
        metrics: {
          totalActivitiesThisWeek: 0,
          avgDailyActivities: 0,
          peakActivityDay: 'No activity',
          activityGrowth: 0,
          activityBreakdown: []
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshData = () => {
    fetchDashboardStats();
  };

  const handleGenerateAnalytics = () => {
    fetchAnalyticsData();
  };

  const handleExportStats = () => {
    fetchStatisticsData();
  };

  const handleTrackActivity = () => {
    fetchActivityData();
  };

  const fetchReportsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await dashboardAPI.getReports();
      setReportsData(data);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    try {
      setGeneratingReport(true);
      setError(null);

      const data = await dashboardAPI.generateReport({
        reportType: 'comprehensive',
      });
      console.log('Report generation status:', data);
      
      // Refresh reports data after generation
      setTimeout(() => {
        fetchReportsData();
      }, 3000);
      
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message);
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleGenerateIndividualReport = async (reportType) => {
    try {
      setError(null);
      
      // Update the specific report status to processing immediately
      setReportsData(prev => ({
        ...prev,
        [reportType]: {
          ...prev[reportType],
          status: 'processing'
        }
      }));
      
      const data = await dashboardAPI.generateReport({
        reportType,
      });
      console.log('Individual report generation status:', data);
      
      // Simulate processing time and then set to ready
      setTimeout(() => {
        setReportsData(prev => ({
          ...prev,
          [reportType]: {
            ...prev[reportType],
            status: 'ready',
            lastGenerated: new Date().toISOString()
          }
        }));
        console.log(`✅ ${reportType} report is now ready for download`);
      }, 2000);
      
    } catch (err) {
      console.error('Error generating individual report:', err);
      setError(err.message);
      
      // Reset status on error
      setReportsData(prev => ({
        ...prev,
        [reportType]: {
          ...prev[reportType],
          status: 'not_submitted'
        }
      }));
    }
  };

  const handleNewAction = () => {
    // TODO: Implement new action functionality
    console.log('Creating new action...');
  };

  // Create new user
  const createUser = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const data = await usersAPI.createUser(userData);
      console.log('User created:', data);
      
      // Close modal and reset form
      setShowUserModal(false);
      setUserForm({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        country: '',
        city: '',
        role: 'student'
      });
      setUserFormErrors({});
      
      // Show success message
      alert(t('userModal.userCreated'));
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle user form submission
  const handleUserSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = {};
    if (!userForm.first_name.trim()) errors.first_name = 'First name is required';
    if (!userForm.last_name.trim()) errors.last_name = 'Last name is required';
    if (!userForm.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(userForm.email)) errors.email = 'Please enter a valid email';
    
    if (Object.keys(errors).length > 0) {
      setUserFormErrors(errors);
      return;
    }
    
    createUser(userForm);
  };

  // Handle user form changes
  const handleUserFormChange = (field, value) => {
    setUserForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (userFormErrors[field]) {
      setUserFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle action card click
  const handleActionCardClick = (action) => {
    console.log('Action card clicked:', action.title);
    console.log('Action object:', action);
    switch (action.title) {
      case 'Add New User':
        console.log('Opening user modal...');
        setShowUserModal(true);
        break;
      case 'Send Notification':
        // TODO: Implement notification sending
        alert('Notification feature coming soon!');
        break;
      case 'Export Users':
        // TODO: Implement user export
        alert('Export feature coming soon!');
        break;
      case 'Create Backup':
        // TODO: Implement backup creation
        alert('Backup feature coming soon!');
        break;
      default:
        console.log('Action clicked:', action.title);
    }
  };

  const handleStartMonitoring = () => {
    fetchMonitoringData();
  };

  const handleGenerateInsights = () => {
    // TODO: Implement insights generation
    console.log('Generating insights...');
  };

  const handleDownloadReport = () => {
    // Existing download report functionality
    console.log('Downloading full report...');
  };
    
  const handleDownloadActivityDetails = () => {
    // Generate Excel file with user analytics data
    const workbook = XLSX.utils.book_new();
    
    // Sample data for the Excel file
    const userAnalyticsData = [
      {
        'Country': 'United States',
        'Students': 1250,
        'Teachers': 85,
        'Parents': 320,
        'Most Active Time': '2:00 PM - 4:00 PM',
        'Peak Engagement Day': 'Wednesday',
        'Pre-exam Activity Spike': '45%'
      },
      {
        'Country': 'United Kingdom',
        'Students': 890,
        'Teachers': 62,
        'Parents': 245,
        'Most Active Time': '3:00 PM - 5:00 PM',
        'Peak Engagement Day': 'Tuesday',
        'Pre-exam Activity Spike': '38%'
      },
      {
        'Country': 'Canada',
        'Students': 720,
        'Teachers': 48,
        'Parents': 180,
        'Most Active Time': '1:00 PM - 3:00 PM',
        'Peak Engagement Day': 'Monday',
        'Pre-exam Activity Spike': '42%'
      },
      {
        'Country': 'Australia',
        'Students': 650,
        'Teachers': 35,
        'Parents': 165,
        'Most Active Time': '4:00 PM - 6:00 PM',
        'Peak Engagement Day': 'Thursday',
        'Pre-exam Activity Spike': '35%'
      },
      {
        'Country': 'Germany',
        'Students': 580,
        'Teachers': 42,
        'Parents': 145,
        'Most Active Time': '2:00 PM - 4:00 PM',
        'Peak Engagement Day': 'Wednesday',
        'Pre-exam Activity Spike': '40%'
      }
    ];

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(userAnalyticsData);
    
    // Set column widths
    const columnWidths = [
      { wch: 15 }, // Country
      { wch: 12 }, // Students
      { wch: 12 }, // Teachers
      { wch: 12 }, // Parents
      { wch: 20 }, // Most Active Time
      { wch: 18 }, // Peak Engagement Day
      { wch: 20 }  // Pre-exam Activity Spike
    ];
    worksheet['!cols'] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'User Analytics');

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Create download link
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Nativelike_User_Analytics_Report.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('Excel file downloaded: Nativelike_User_Analytics_Report.xlsx');
  };

  const handleDownloadMonthlyReport = () => {
    const workbook = XLSX.utils.book_new();
    
    const monthlyData = [
      {
        'Month': 'January 2024',
        'Total Users': 2847,
        'New Signups': 234,
        'Active Users': 1892,
        'Revenue': '$12,450',
        'Courses Completed': 156,
        'Avg Session Time': '24 min'
      },
      {
        'Month': 'February 2024',
        'Total Users': 3120,
        'New Signups': 273,
        'Active Users': 2103,
        'Revenue': '$14,230',
        'Courses Completed': 189,
        'Avg Session Time': '26 min'
      },
      {
        'Month': 'March 2024',
        'Total Users': 3456,
        'New Signups': 336,
        'Active Users': 2341,
        'Revenue': '$16,780',
        'Courses Completed': 223,
        'Avg Session Time': '28 min'
      },
      {
        'Month': 'April 2024',
        'Total Users': 3789,
        'New Signups': 333,
        'Active Users': 2567,
        'Revenue': '$18,920',
        'Courses Completed': 267,
        'Avg Session Time': '29 min'
      },
      {
        'Month': 'May 2024',
        'Total Users': 4123,
        'New Signups': 334,
        'Active Users': 2789,
        'Revenue': '$21,450',
        'Courses Completed': 312,
        'Avg Session Time': '31 min'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(monthlyData);
    worksheet['!cols'] = [
      { wch: 15 }, // Month
      { wch: 12 }, // Total Users
      { wch: 12 }, // New Signups
      { wch: 12 }, // Active Users
      { wch: 12 }, // Revenue
      { wch: 15 }, // Courses Completed
      { wch: 15 }  // Avg Session Time
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Monthly Report');
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Nativelike_Monthly_Report.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('Monthly report downloaded');
  };

  const handleDownloadAnalyticsReport = () => {
    const workbook = XLSX.utils.book_new();
    
    const analyticsData = [
      {
        'Metric': 'Page Views',
        'Today': 12450,
        'This Week': 89234,
        'This Month': 345678,
        'Growth': '+12%'
      },
      {
        'Metric': 'Unique Visitors',
        'Today': 3456,
        'This Week': 23456,
        'This Month': 89234,
        'Growth': '+8%'
      },
      {
        'Metric': 'Bounce Rate',
        'Today': '23%',
        'This Week': '25%',
        'This Month': '24%',
        'Growth': '-2%'
      },
      {
        'Metric': 'Avg Session Duration',
        'Today': '4m 32s',
        'This Week': '4m 15s',
        'This Month': '4m 28s',
        'Growth': '+4%'
      },
      {
        'Metric': 'Conversion Rate',
        'Today': '3.2%',
        'This Week': '3.1%',
        'This Month': '3.3%',
        'Growth': '+6%'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(analyticsData);
    worksheet['!cols'] = [
      { wch: 20 }, // Metric
      { wch: 12 }, // Today
      { wch: 12 }, // This Week
      { wch: 12 }, // This Month
      { wch: 10 }  // Growth
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Analytics Report');
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Nativelike_Analytics_Report.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('Analytics report downloaded');
  };

  const handleDownloadFinancialReport = () => {
    const workbook = XLSX.utils.book_new();
    
    const financialData = [
      {
        'Revenue Stream': 'Course Subscriptions',
        'January': 12450,
        'February': 14230,
        'March': 16780,
        'April': 18920,
        'May': 21450,
        'Total': 83830
      },
      {
        'Revenue Stream': 'Live Classes',
        'January': 8900,
        'February': 10200,
        'March': 11800,
        'April': 13400,
        'May': 15200,
        'Total': 59500
      },
      {
        'Revenue Stream': 'Premium Features',
        'January': 5600,
        'February': 6200,
        'March': 7200,
        'April': 8100,
        'May': 9200,
        'Total': 36300
      },
      {
        'Revenue Stream': 'Exam Fees',
        'January': 3200,
        'February': 3800,
        'March': 4200,
        'April': 4800,
        'May': 5400,
        'Total': 21400
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(financialData);
    worksheet['!cols'] = [
      { wch: 20 }, // Revenue Stream
      { wch: 12 }, // January
      { wch: 12 }, // February
      { wch: 12 }, // March
      { wch: 12 }, // April
      { wch: 12 }, // May
      { wch: 12 }  // Total
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Financial Report');
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Nativelike_Financial_Report.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('Financial report downloaded');
  };

  const handleDownloadEducationReport = () => {
    const workbook = XLSX.utils.book_new();
    
    const educationData = [
      {
        'Course': 'English Grammar',
        'Enrolled Students': 1245,
        'Completion Rate': '78%',
        'Avg Score': '85%',
        'Active Sessions': 892,
        'Teacher Rating': '4.8/5'
      },
      {
        'Course': 'Vocabulary Building',
        'Enrolled Students': 987,
        'Completion Rate': '82%',
        'Avg Score': '88%',
        'Active Sessions': 734,
        'Teacher Rating': '4.9/5'
      },
      {
        'Course': 'Reading Comprehension',
        'Enrolled Students': 756,
        'Completion Rate': '75%',
        'Avg Score': '82%',
        'Active Sessions': 567,
        'Teacher Rating': '4.7/5'
      },
      {
        'Course': 'Writing Skills',
        'Enrolled Students': 634,
        'Completion Rate': '80%',
        'Avg Score': '86%',
        'Active Sessions': 445,
        'Teacher Rating': '4.8/5'
      },
      {
        'Course': 'Speaking Practice',
        'Enrolled Students': 892,
        'Completion Rate': '85%',
        'Avg Score': '90%',
        'Active Sessions': 678,
        'Teacher Rating': '4.9/5'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(educationData);
    worksheet['!cols'] = [
      { wch: 20 }, // Course
      { wch: 15 }, // Enrolled Students
      { wch: 15 }, // Completion Rate
      { wch: 12 }, // Avg Score
      { wch: 15 }, // Active Sessions
      { wch: 15 }  // Teacher Rating
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Education Report');
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Nativelike_Education_Report.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('Education report downloaded');
  };

  const handleDownloadUserReport = () => {
    const workbook = XLSX.utils.book_new();
    
    const userData = [
      {
        'User Type': 'Students',
        'Total Users': 3456,
        'Active This Month': 2892,
        'New This Month': 234,
        'Premium Users': 1234,
        'Avg Age': '24',
        'Top Country': 'United States'
      },
      {
        'User Type': 'Teachers',
        'Total Users': 234,
        'Active This Month': 198,
        'New This Month': 23,
        'Premium Users': 156,
        'Avg Age': '32',
        'Top Country': 'United Kingdom'
      },
      {
        'User Type': 'Parents',
        'Total Users': 892,
        'Active This Month': 734,
        'New This Month': 67,
        'Premium Users': 445,
        'Avg Age': '41',
        'Top Country': 'Canada'
      },
      {
        'User Type': 'Administrators',
        'Total Users': 12,
        'Active This Month': 12,
        'New This Month': 2,
        'Premium Users': 12,
        'Avg Age': '35',
        'Top Country': 'United States'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(userData);
    worksheet['!cols'] = [
      { wch: 15 }, // User Type
      { wch: 12 }, // Total Users
      { wch: 15 }, // Active This Month
      { wch: 12 }, // New This Month
      { wch: 12 }, // Premium Users
      { wch: 10 }, // Avg Age
      { wch: 15 }  // Top Country
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'User Report');
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Nativelike_User_Report.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('User report downloaded');
  };

  const handleDownloadSecurityReport = () => {
    const workbook = XLSX.utils.book_new();
    
    const securityData = [
      {
        'Security Check': 'User Authentication',
        'Status': '✅ Passed',
        'Last Check': '2024-01-15',
        'Risk Level': 'Low',
        'Action Required': 'None'
      },
      {
        'Security Check': 'Data Encryption',
        'Status': '✅ Passed',
        'Last Check': '2024-01-15',
        'Risk Level': 'Low',
        'Action Required': 'None'
      },
      {
        'Security Check': 'SQL Injection Protection',
        'Status': '✅ Passed',
        'Last Check': '2024-01-15',
        'Risk Level': 'Low',
        'Action Required': 'None'
      },
      {
        'Security Check': 'XSS Protection',
        'Status': '✅ Passed',
        'Last Check': '2024-01-15',
        'Risk Level': 'Low',
        'Action Required': 'None'
      },
      {
        'Security Check': 'Session Management',
        'Status': '✅ Passed',
        'Last Check': '2024-01-15',
        'Risk Level': 'Low',
        'Action Required': 'None'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(securityData);
    worksheet['!cols'] = [
      { wch: 20 }, // Security Check
      { wch: 12 }, // Status
      { wch: 15 }, // Last Check
      { wch: 12 }, // Risk Level
      { wch: 15 }  // Action Required
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Security Report');
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Nativelike_Security_Report.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('Security report downloaded');
  };
    
  const handleOpenActivityChart = () => {
    // Fetch latest activity chart data before opening
    fetchActivityChartData();
    setIsActivityChartModalOpen(true);
  };

  // Modal handlers
  const handleOpenUserGrowthModal = () => {
    setIsUserGrowthModalOpen(true);
  };

  const handleCloseUserGrowthModal = () => {
    setIsUserGrowthModalOpen(false);
  };

  const handleCloseActivityChartModal = () => {
    setIsActivityChartModalOpen(false);
  };

  useEffect(() => {
    // Load initial dashboard data
    fetchDashboardStats();
  }, []);

  // Load data when tab changes
  useEffect(() => {
    switch (activeTab) {
      case 'analytics':
        fetchAnalyticsData();
        break;
      case 'statistics':
        fetchStatisticsData();
        break;
      case 'activity':
        fetchActivityData();
        break;
      case 'reports':
        fetchReportsData();
        break;
      case 'monitoring':
        fetchMonitoringData();
        break;
      default:
        break;
    }
  }, [activeTab]);

  // Auto-refresh monitoring data every 30 seconds when on monitoring tab
  useEffect(() => {
    if (activeTab === 'monitoring') {
      const interval = setInterval(() => {
        console.log('Auto-refreshing monitoring data...');
        fetchMonitoringData();
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    }
  }, [activeTab]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('search.dashboard')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button 
                className="add-user-btn"
                onClick={handleRefreshData}
                disabled={loading}
              >
                <span className="material-icons">refresh</span>
                {loading ? t('buttons.loading') : t('buttons.refreshData')}
              </button>
            </div>
            
            {error && (
              <div className="error-message">
                <span className="material-icons">error</span>
                {error}
              </div>
            )}
            
            <div className="cards-container">
              <h3>{t('overview.title')}</h3>
              <p className="overview-live-note" style={{ marginTop: 4, marginBottom: 8, fontSize: '0.85rem', color: 'var(--text-secondary, #666)' }}>
                {t('overview.liveStatsNote')}
              </p>
              {searchTerm && (
                <div className="search-results-info">
                  <span className="material-icons">search</span>
                  <span>{t('overview.searchingFor')} "{searchTerm}"</span>
              </div>
              )}
              <div className="cards-grid">
                {(() => {
                  const dashboardCards = [
                    {
                      title: t('overview.totalUsers.title'),
                      description: t('overview.totalUsers.description'),
                      value: (dashboardStats.totalUsers || 0).toLocaleString(),
                      icon: 'people'
                    },
                    {
                      title: t('overview.activeUsers.title'),
                      description: t('overview.activeUsers.description'),
                      value: (dashboardStats.activeUsers || 0).toLocaleString(),
                      icon: 'person',
                      onClick: handleOpenActiveUsers,
                      _focusKey: 'active_users',
                    },
                    {
                      title: t('overview.newSignups.title'),
                      description: t('overview.newSignups.description'),
                      value: (dashboardStats.newSignups || 0).toLocaleString(),
                      icon: 'person_add'
                    },
                    {
                      title: t('overview.premiumUsers.title'),
                      description: t('overview.premiumUsers.description'),
                      value: (dashboardStats.premiumUsers || 0).toLocaleString(),
                      icon: 'star'
                    },
                    {
                      title: t('overview.teachers.title'),
                      description: t('overview.teachers.description'),
                      value: (dashboardStats.teachers || 0).toLocaleString(),
                      icon: 'school'
                    },
                    {
                      title: t('overview.students.title'),
                      description: t('overview.students.description'),
                      value: (dashboardStats.students || 0).toLocaleString(),
                      icon: 'person_outline'
                    },
                    {
                      title: t('overview.totalArticles.title'),
                      description: t('overview.totalArticles.description'),
                      value: (dashboardStats.totalArticles || 0).toLocaleString(),
                      icon: 'article'
                    },
                    {
                      title: t('overview.totalCourses.title'),
                      description: t('overview.totalCourses.description'),
                      value: (dashboardStats.totalCourses || 0).toLocaleString(),
                      icon: 'school'
                    },
                    {
                      title: t('overview.liveClasses.title'),
                      description: t('overview.liveClasses.description'),
                      value: (dashboardStats.totalLiveClasses || 0).toLocaleString(),
                      icon: 'event'
                    },
                    {
                      title: t('overview.totalEarnings.title'),
                      description: t('overview.totalEarnings.description'),
                      value: (dashboardStats.totalEarnings || 0).toLocaleString(),
                      icon: 'attach_money'
                    }
                  ];

                  const filteredCards = filterCards(dashboardCards, searchTerm);

                  return filteredCards.map((card, index) => (
                    <div
                      key={index}
                      className={`overview-card ${card.onClick ? 'clickable' : ''}`}
                      onClick={card.onClick}
                      ref={card._focusKey === 'active_users' ? activeUsersCardRef : null}
                      style={{ cursor: card.onClick ? 'pointer' : 'default' }}
                    >
                      <div className="card-header">
                        <span className="material-icons card-icon">{card.icon}</span>
                        <h4>{card.title}</h4>
                      </div>
                      <div className="card-value">{card.value}</div>
                      <div className="card-description">{card.description}</div>
                    </div>
                  ));
                })()}
              </div>
              
                <div className="recent-activity">
                  <h4>{t('overview.recentActivity.title')}</h4>
                {(() => {
                  const filteredActivities = filterActivities(dashboardStats.recentActivity || [], searchTerm);
                  
                  if (filteredActivities.length > 0) {
                    return (
                  <div className="activity-list">
                        {filteredActivities.map((activity, index) => (
                      <div key={index} className="activity-item">
                        <span className="material-icons">
                          {activity.type === 'vocabulary' ? 'book' : 
                           activity.type === 'article' ? 'article' : 'person'}
                        </span>
                        <span>{activity.action} {activity.item}</span>
                        <span className="time">{activity.time}</span>
                      </div>
                    ))}
                  </div>
                    );
                  } else if (dashboardStats.recentActivity && dashboardStats.recentActivity.length > 0) {
                    return (
                      <div className="no-activity-message">
                        <span className="material-icons">search</span>
                        <p>{t('overview.recentActivity.noMatch')}</p>
                </div>
                    );
                  } else {
                    return (
                      <div className="no-activity-message">
                        <span className="material-icons">info</span>
                        <p>{t('overview.recentActivity.noActivities')}</p>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          </div>
        );

      case 'analytics':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('search.analytics')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button 
                className="add-user-btn"
                onClick={handleGenerateAnalytics}
                disabled={loading}
              >
                <span className="material-icons">analytics</span>
                {loading ? t('buttons.generating') : t('buttons.generateAnalytics')}
              </button>
            </div>

            <div className="cards-container">
              <h3>{t('analytics.title')}</h3>
              <div className="cards-grid">
                {(() => {
                  const analyticsCards = [
                    {
                      title: t('analytics.userGrowthChart.title'),
                      value: '📈',
                      description: t('analytics.userGrowthChart.description'),
                      icon: 'trending_up'
                    },
                    {
                      title: t('analytics.todaysActive.title'),
                      value: analyticsData.todayActive || 0,
                      description: t('analytics.todaysActive.description'),
                      icon: 'schedule'
                    },
                    {
                      title: t('analytics.weekActive.title'),
                      value: analyticsData.weekActive || 0,
                      description: t('analytics.weekActive.description'),
                      icon: 'calendar_week'
                    },
                    {
                      title: t('analytics.monthsActive.title'),
                      value: analyticsData.monthActive || 0,
                      description: t('analytics.monthsActive.description'),
                      icon: 'calendar_month'
                    },
                    {
                      title: t('analytics.avgSession.title'),
                      value: `${analyticsData.avgSession || 0} min`,
                      description: t('analytics.avgSession.description'),
                      icon: 'timer'
                    },
                    {
                      title: t('analytics.downloadFullReport.title'),
                      value: '📥',
                      description: t('analytics.downloadFullReport.description'),
                      icon: 'download'
                    }
                  ];

                  const filteredCards = filterCards(analyticsCards, searchTerm);
                  
                  if (filteredCards.length > 0) {
                    return filteredCards.map((card, index) => (
                      <div 
                        key={index} 
                        className="overview-card"
                        onClick={card.title === t('analytics.userGrowthChart.title') ? handleOpenUserGrowthModal : 
                               card.title === t('analytics.todaysActive.title') ? handleOpenActiveUsersToday :
                               card.title === t('analytics.weekActive.title') ? handleOpenWeeklyActiveUsers :
                               card.title === t('analytics.monthsActive.title') ? handleOpenMonthlyActiveUsers :
                               card.title === t('analytics.downloadFullReport.title') ? handleDownloadReport : undefined}
                        style={{ cursor: (card.title === t('analytics.userGrowthChart.title') || card.title === t('analytics.todaysActive.title') || card.title === t('analytics.weekActive.title') || card.title === t('analytics.monthsActive.title') || card.title === t('analytics.downloadFullReport.title')) ? 'pointer' : 'default' }}
                      >
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">{card.value}</div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  } else if (searchTerm.trim()) {
                    return (
                      <div className="no-search-results">
                        <span className="material-icons">search_off</span>
                        <p>{t('analytics.noMatch')} "{searchTerm}"</p>
                      </div>
                    );
                  } else {
                    return analyticsCards.map((card, index) => (
                      <div 
                        key={index} 
                        className="overview-card"
                        onClick={card.title === t('analytics.userGrowthChart.title') ? handleOpenUserGrowthModal : 
                               card.title === t('analytics.todaysActive.title') ? handleOpenActiveUsersToday :
                               card.title === t('analytics.weekActive.title') ? handleOpenWeeklyActiveUsers :
                               card.title === t('analytics.monthsActive.title') ? handleOpenMonthlyActiveUsers :
                               card.title === t('analytics.downloadFullReport.title') ? handleDownloadReport : undefined}
                        style={{ cursor: (card.title === t('analytics.userGrowthChart.title') || card.title === t('analytics.todaysActive.title') || card.title === t('analytics.weekActive.title') || card.title === t('analytics.monthsActive.title') || card.title === t('analytics.downloadFullReport.title')) ? 'pointer' : 'default' }}
                      >
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">{card.value}</div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  }
                })()}
              </div>
            </div>
          </div>
        );

      case 'statistics':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('search.statistics')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>

              <button 
                className="add-user-btn"
                onClick={handleExportStats}
                disabled={loading}
              >
                <span className="material-icons">bar_chart</span>
                {loading ? t('buttons.loading') : t('buttons.exportStats')}
              </button>
            </div>

            {error && (
              <div className="error-message">
                <span className="material-icons">error</span>
                {error}
              </div>
            )}
            
            <div className="cards-container">
              <h3>{t('statistics.title')}</h3>
              {loading && (
                <div className="loading-message">
                  <span className="material-icons">hourglass_empty</span>
                  {t('statistics.loading')}
                </div>
              )}
              <div className="cards-grid">
                {(() => {
                  const statisticsCards = [
                    {
                      title: t('statistics.growthRate.title'),
                      value: `+${statisticsData.growthRate || 0}%`,
                      description: t('statistics.growthRate.description'),
                      icon: 'trending_up'
                    },
                    {
                      title: t('statistics.avgSession.title'),
                      value: `${statisticsData.avgSession || 0} min`,
                      description: t('statistics.avgSession.description'),
                      icon: 'schedule'
                    },
                    {
                      title: t('statistics.retentionRate.title'),
                      value: `${statisticsData.retentionRate || 0}%`,
                      description: t('statistics.retentionRate.description'),
                      icon: 'repeat'
                    },
                    {
                      title: t('statistics.performance.title'),
                      value: `${statisticsData.performance || 0}%`,
                      description: t('statistics.performance.description'),
                      icon: 'speed'
                    },
                    {
                      title: t('statistics.errorRate.title'),
                      value: `${statisticsData.errorRate || 0}%`,
                      description: t('statistics.errorRate.description'),
                      icon: 'error'
                    },
                    {
                      title: t('statistics.satisfaction.title'),
                      value: `${statisticsData.satisfaction || 0}/5`,
                      description: t('statistics.satisfaction.description'),
                      icon: 'thumb_up'
                    }
                  ];

                  const filteredCards = filterCards(statisticsCards, searchTerm);
                  
                  if (filteredCards.length > 0) {
                    return filteredCards.map((card, index) => (
                      <div key={index} className="overview-card">
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">{card.value}</div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  } else if (searchTerm.trim()) {
                    return (
                      <div className="no-search-results">
                        <span className="material-icons">search_off</span>
                        <p>{t('statistics.noMatch')} "{searchTerm}"</p>
                      </div>
                    );
                  } else {
                    return statisticsCards.map((card, index) => (
                      <div key={index} className="overview-card">
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">{card.value}</div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  }
                })()}
              </div>
            </div>
          </div>
        );

      case 'activity':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('search.activity')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>

              <button 
                className="add-user-btn"
                onClick={handleTrackActivity}
                disabled={loading}
              >
                <span className="material-icons">trending_up</span>
                {loading ? t('buttons.tracking') : t('buttons.trackActivity')}
              </button>
            </div>

            <div className="cards-container">
              <h3>{t('activity.title')}</h3>
              <div className="cards-grid">
                {(() => {
                  const activityCards = [
                    {
                      title: t('activity.realTimeActivity.title'),
                      value: '📊',
                      description: t('activity.realTimeActivity.description'),
                      icon: 'visibility',
                      onClick: () => handleOpenActivityChart()
                    },
                    {
                      title: t('activity.activeSessions.title'),
                      value: activityData.activeSessions || 0,
                      description: t('activity.activeSessions.description'),
                      icon: 'schedule'
                    },
                    {
                      title: t('activity.pageViews.title'),
                      value: (activityData.pageViews || 0).toLocaleString(),
                      description: t('activity.pageViews.description'),
                      icon: 'pageview'
                    },
                    {
                      title: t('activity.bounceRate.title'),
                      value: `${activityData.bounceRate || 0}%`,
                      description: t('activity.bounceRate.description'),
                      icon: 'bounce'
                    },
                    {
                      title: t('activity.conversionRate.title'),
                      value: `${activityData.conversionRate || 0}%`,
                      description: t('activity.conversionRate.description'),
                      icon: 'trending_up'
                    },
                    {
                      title: t('activity.activityDetails.title'),
                      value: '📈',
                      description: t('activity.activityDetails.description'),
                      icon: 'analytics',
                      onClick: () => handleDownloadActivityDetails()
                    }
                  ];

                  const filteredCards = filterCards(activityCards, searchTerm);
                  
                  if (filteredCards.length > 0) {
                    return filteredCards.map((card, index) => (
                      <div 
                        key={index} 
                        className={`overview-card ${card.onClick ? 'clickable' : ''}`}
                        onClick={card.onClick}
                        style={{ cursor: card.onClick ? 'pointer' : 'default' }}
                      >
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">{card.value}</div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  } else if (searchTerm.trim()) {
                    return (
                      <div className="no-search-results">
                        <span className="material-icons">search_off</span>
                        <p>{t('activity.noMatch')} "{searchTerm}"</p>
                      </div>
                    );
                  } else {
                    return activityCards.map((card, index) => (
                      <div 
                        key={index} 
                        className={`overview-card ${card.onClick ? 'clickable' : ''}`}
                        onClick={card.onClick}
                        style={{ cursor: card.onClick ? 'pointer' : 'default' }}
                      >
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">{card.value}</div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  }
                })()}
              </div>
            </div>
          </div>
        );

      case 'reports':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('search.reports')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button 
                className="add-user-btn"
                onClick={handleGenerateReport}
                disabled={generatingReport}
              >
                <span className="material-icons">assessment</span>
                {generatingReport ? t('buttons.generatingReport') : t('buttons.generateReport')}
              </button>
            </div>

            <div className="cards-container">
              <h3>{t('reports.title')}</h3>
              <div className="cards-grid">
                {(() => {
                  const reportsCards = [
                    {
                      title: t('reports.monthlyReport.title'),
                      value: '📥 Download',
                      description: t('reports.monthlyReport.description'),
                      icon: 'description',
                      onClick: () => handleDownloadMonthlyReport()
                    },
                    {
                      title: t('reports.analyticsReport.title'),
                      value: '📥 Download',
                      description: t('reports.analyticsReport.description'),
                      icon: 'pie_chart',
                      onClick: () => handleDownloadAnalyticsReport()
                    },
                    {
                      title: t('reports.financialReport.title'),
                      value: '📥 Download',
                      description: t('reports.financialReport.description'),
                      icon: 'account_balance',
                      onClick: () => handleDownloadFinancialReport()
                    },
                    {
                      title: t('reports.securityReport.title'),
                      value: '📥 Download',
                      description: t('reports.securityReport.description'),
                      icon: 'security',
                      onClick: () => handleDownloadSecurityReport()
                    },
                    {
                      title: t('reports.educationReport.title'),
                      value: '📥 Download',
                      description: t('reports.educationReport.description'),
                      icon: 'school',
                      onClick: () => handleDownloadEducationReport()
                    },
                    {
                      title: t('reports.userReport.title'),
                      value: '📥 Download',
                      description: t('reports.userReport.description'),
                      icon: 'people',
                      onClick: () => handleDownloadUserReport()
                    }
                  ];

                  const filteredCards = filterCards(reportsCards, searchTerm);
                  
                  if (filteredCards.length > 0) {
                    return filteredCards.map((card, index) => (
                      <div 
                        key={index} 
                        className={`overview-card ${card.onClick ? 'clickable' : ''}`}
                        onClick={card.onClick}
                        style={{ cursor: card.onClick ? 'pointer' : 'default' }}
                      >
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">
                          {card.value}
                        </div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  } else if (searchTerm.trim()) {
                    return (
                      <div className="no-search-results">
                        <span className="material-icons">search_off</span>
                        <p>{t('reports.noMatch')} "{searchTerm}"</p>
                      </div>
                    );
                  } else {
                    return reportsCards.map((card, index) => (
                      <div 
                        key={index} 
                        className={`overview-card ${card.onClick ? 'clickable' : ''}`}
                        onClick={card.onClick}
                        style={{ cursor: card.onClick ? 'pointer' : 'default' }}
                      >
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">
                          {card.value}
                        </div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  }
                })()}
              </div>
            </div>
          </div>
        );

      case 'actions':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('search.actions')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button 
                className="add-user-btn"
                onClick={handleNewAction}
              >
                <span className="material-icons">touch_app</span>
                {t('buttons.newAction')}
              </button>
            </div>

            <div className="cards-container">
              <h3>{t('actions.title')}</h3>
              <div className="cards-grid">
                {(() => {
                  const actionsCards = [
                    {
                      title: t('actions.addNewUser.title'),
                      value: '👤',
                      description: t('actions.addNewUser.description'),
                      icon: 'person_add'
                    },
                    {
                      title: t('actions.sendNotification.title'),
                      value: '📢',
                      description: t('actions.sendNotification.description'),
                      icon: 'message'
                    },
                    {
                      title: t('actions.exportUsers.title'),
                      value: '📊',
                      description: t('actions.exportUsers.description'),
                      icon: 'download'
                    },
                    {
                      title: t('actions.createBackup.title'),
                      value: '💾',
                      description: t('actions.createBackup.description'),
                      icon: 'backup'
                    }
                  ];

                  const filteredCards = filterCards(actionsCards, searchTerm);
                  
                  if (filteredCards.length > 0) {
                    return filteredCards.map((card, index) => (
                      <div 
                        key={index} 
                        className="overview-card"
                        onClick={() => handleActionCardClick(card)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">{card.value}</div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  } else if (searchTerm.trim()) {
                    return (
                      <div className="no-search-results">
                        <span className="material-icons">search_off</span>
                        <p>{t('actions.noMatch')} "{searchTerm}"</p>
                      </div>
                    );
                  } else {
                    return actionsCards.map((card, index) => (
                      <div 
                        key={index} 
                        className="overview-card"
                        onClick={() => handleActionCardClick(card)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">{card.value}</div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  }
                })()}
              </div>
            </div>
          </div>
        );

      case 'monitoring':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('search.monitoring')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button 
                className="add-user-btn"
                onClick={handleStartMonitoring}
                disabled={loading}
              >
                <span className="material-icons">monitor</span>
                {loading ? t('buttons.refreshing') : t('buttons.refreshData')}
              </button>
            </div>

            <div className="cards-container">
              <h3>{t('monitoring.title')}</h3>
              {lastMonitoringUpdate && (
                <div style={{ 
                  textAlign: 'center', 
                  fontSize: '12px', 
                  color: '#666', 
                  marginBottom: '10px',
                  fontStyle: 'italic'
                }}>
                  {t('monitoring.lastUpdated')} {lastMonitoringUpdate.toLocaleTimeString()}
                </div>
              )}
              <div className="cards-grid">
                {(() => {
                  const monitoringCards = [
                    {
                      title: t('monitoring.cpuUsage.title'),
                      value: `${monitoringData.cpuUsage || 0}%`,
                      description: t('monitoring.cpuUsage.description'),
                      icon: 'memory',
                      status: monitoringData.cpuUsage > 80 ? 'warning' : monitoringData.cpuUsage > 60 ? 'info' : 'good'
                    },
                    {
                      title: t('monitoring.memoryUsage.title'),
                      value: `${monitoringData.memoryUsage || 0}%`,
                      description: `${monitoringData.freeMemory || 0}${t('monitoring.memoryUsage.description')}${monitoringData.totalMemory || 0}GB`,
                      icon: 'storage',
                      status: monitoringData.memoryUsage > 85 ? 'warning' : monitoringData.memoryUsage > 70 ? 'info' : 'good'
                    },
                    {
                      title: t('monitoring.network.title'),
                      value: monitoringData.networkStatus || 'Unknown',
                      description: t('monitoring.network.description'),
                      icon: 'dns',
                      status: monitoringData.networkStatus === 'Connected' ? 'good' : 'warning'
                    },
                    {
                      title: t('monitoring.database.title'),
                      value: monitoringData.databaseStatus || 'Unknown',
                      description: t('monitoring.database.description'),
                      icon: 'database',
                      status: monitoringData.databaseStatus === 'Healthy' ? 'good' : 'warning'
                    },
                    {
                      title: t('monitoring.security.title'),
                      value: monitoringData.securityStatus || 'Unknown',
                      description: t('monitoring.security.description'),
                      icon: 'security',
                      status: monitoringData.securityStatus === 'Protected' ? 'good' : 'warning'
                    },
                    {
                      title: t('monitoring.systemUptime.title'),
                      value: monitoringData.uptime || '0h',
                      description: t('monitoring.systemUptime.description'),
                      icon: 'wifi',
                      status: 'good'
                    },
                    {
                      title: t('monitoring.processUptime.title'),
                      value: monitoringData.processUptime || '0h',
                      description: t('monitoring.processUptime.description'),
                      icon: 'schedule',
                      status: 'good'
                    },
                    {
                      title: t('monitoring.platform.title'),
                      value: monitoringData.platform || 'Unknown',
                      description: `${t('monitoring.platform.description')} ${monitoringData.nodeVersion || 'Unknown'}`,
                      icon: 'computer',
                      status: 'info'
                    }
                  ];

                  const filteredCards = filterCards(monitoringCards, searchTerm);
                  
                  if (filteredCards.length > 0) {
                    return filteredCards.map((card, index) => (
                      <div key={index} className={`overview-card ${card.status ? `status-${card.status}` : ''}`}>
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">{card.value}</div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  } else if (searchTerm.trim()) {
                    return (
                      <div className="no-search-results">
                        <span className="material-icons">search_off</span>
                        <p>{t('monitoring.noMatch')} "{searchTerm}"</p>
                      </div>
                    );
                  } else {
                    return monitoringCards.map((card, index) => (
                      <div key={index} className={`overview-card ${card.status ? `status-${card.status}` : ''}`}>
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">{card.value}</div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  }
                })()}
              </div>
            </div>
          </div>
        );

      case 'insights':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('search.insights')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button 
                className="add-user-btn"
                onClick={handleGenerateInsights}
              >
                <span className="material-icons">lightbulb</span>
                {t('buttons.generateInsights')}
              </button>
            </div>

            <div className="cards-container">
              <h3>{t('insights.title')}</h3>
              <div className="cards-grid">
                {(() => {
                  const insightsCards = [
                    {
                      title: t('insights.userInsights.title'),
                      value: '📊',
                      description: t('insights.userInsights.description'),
                      icon: 'analytics'
                    },
                    {
                      title: t('insights.peakUsage.title'),
                      value: '2-4 PM',
                      description: t('insights.peakUsage.description'),
                      icon: 'schedule'
                    },
                    {
                      title: t('insights.mostActive.title'),
                      value: 'Wednesday',
                      description: t('insights.mostActive.description'),
                      icon: 'calendar_today'
                    },
                    {
                      title: t('insights.popularFeature.title'),
                      value: 'Live Learning',
                      description: t('insights.popularFeature.description'),
                      icon: 'star'
                    },
                    {
                      title: t('insights.satisfaction.title'),
                      value: '4.7/5',
                      description: t('insights.satisfaction.description'),
                      icon: 'thumb_up'
                    },
                    {
                      title: t('insights.newInsights.title'),
                      value: '💡',
                      description: t('insights.newInsights.description'),
                      icon: 'lightbulb'
                    }
                  ];

                  const filteredCards = filterCards(insightsCards, searchTerm);
                  
                  if (filteredCards.length > 0) {
                    return filteredCards.map((card, index) => (
                      <div key={index} className="overview-card">
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">{card.value}</div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  } else if (searchTerm.trim()) {
                    return (
                      <div className="no-search-results">
                        <span className="material-icons">search_off</span>
                        <p>{t('insights.noMatch')} "{searchTerm}"</p>
                      </div>
                    );
                  } else {
                    return insightsCards.map((card, index) => (
                      <div key={index} className="overview-card">
                        <div className="card-header">
                          <span className="material-icons card-icon">{card.icon}</span>
                          <h4>{card.title}</h4>
                        </div>
                        <div className="card-value">{card.value}</div>
                        <div className="card-description">{card.description}</div>
                      </div>
                    ));
                  }
                })()}
              </div>
            </div>
          </div>
        );

      default:
        return <div className="tab-content">{t('overview')}</div>;
    }
  };

  return (
    <div className="section overview-section">
      <h2>Overview</h2>
      
      {/* Horizontal Tabs */}
      <div className="user-management-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="material-icons">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* User Growth Modal */}
      <UserGrowthModal
        isOpen={isUserGrowthModalOpen}
        onClose={handleCloseUserGrowthModal}
      />

      {/* Activity Chart Modal */}
      <ActivityChartModal
        isOpen={isActivityChartModalOpen}
        onClose={handleCloseActivityChartModal}
        data={activityChartData}
      />

      {/* Active Users Modal */}
      {showActiveUsersModal && (
        <div className="modal-overlay" onClick={() => setShowActiveUsersModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
            <div className="modal-header">
              <h3>
                {activeUsersModalMode === 'today'
                  ? "Today's Active Users"
                  : activeUsersModalMode === 'window'
                    ? (activeUsersWindowDays >= 30
                        ? `Monthly Active Users (last ${activeUsersWindowDays} days)`
                        : `Weekly Active Users (last ${activeUsersWindowDays} days)`)
                    : `Active Users (last ${activeUsersWindowMinutes} min)`}
              </h3>
              <button className="close-btn" onClick={() => setShowActiveUsersModal(false)} aria-label="Close" type="button">×</button>
            </div>

            <div className="modal-body">
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginBottom: 12 }}>
                <button
                  className="add-user-btn"
                  type="button"
                  onClick={
                    activeUsersModalMode === 'today'
                      ? fetchActiveUsersTodayList
                      : activeUsersModalMode === 'window'
                        ? (() => fetchActiveUsersWindowList(activeUsersWindowDays))
                        : fetchActiveUsersList
                  }
                  disabled={loadingActiveUsers}
                >
                  <span className="material-icons">refresh</span>
                  {loadingActiveUsers ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {activeUsersList.length === 0 ? (
                <div className="empty-state" style={{ textAlign: 'center', padding: 16 }}>
                  <span className="material-icons">info</span>
                  <p style={{ marginBottom: 0 }}>
                    {activeUsersModalMode === 'today'
                      ? 'No users have logged in today.'
                      : activeUsersModalMode === 'window'
                        ? `No users have logged in during the last ${activeUsersWindowDays} days.`
                        : 'No active users right now.'}
                  </p>
                </div>
              ) : (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeUsersList.map((u) => (
                      <tr key={u.id}>
                        <td>{u.id}</td>
                        <td>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}</td>
                        <td>{u.email || '—'}</td>
                        <td>{u.role || '—'}</td>
                        <td title={u.last_login}>{u.last_login ? new Date(u.last_login).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Creation Modal */}
      {showUserModal && (
        <div className="user-form-overlay">
          <div className="user-form">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>{t('userModal.title')}</h3>
              <button 
                onClick={() => {
                  setShowUserModal(false);
                  setUserForm({
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    country: '',
                    city: '',
                    role: 'student'
                  });
                  setUserFormErrors({});
                }}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUserSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('userModal.firstName')}</label>
                  <input
                    type="text"
                    value={userForm.first_name}
                    onChange={(e) => handleUserFormChange('first_name', e.target.value)}
                    placeholder={t('userModal.firstNamePlaceholder')}
                    className={userFormErrors.first_name ? 'error' : ''}
                  />
                  {userFormErrors.first_name && (
                    <span className="error-message">{userFormErrors.first_name}</span>
                  )}
                </div>
                
                <div className="form-group">
                  <label>{t('userModal.lastName')}</label>
                  <input
                    type="text"
                    value={userForm.last_name}
                    onChange={(e) => handleUserFormChange('last_name', e.target.value)}
                    placeholder={t('userModal.lastNamePlaceholder')}
                    className={userFormErrors.last_name ? 'error' : ''}
                  />
                  {userFormErrors.last_name && (
                    <span className="error-message">{userFormErrors.last_name}</span>
                  )}
                </div>
              </div>
              
              <div className="form-group">
                <label>{t('userModal.email')}</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => handleUserFormChange('email', e.target.value)}
                  placeholder={t('userModal.emailPlaceholder')}
                  className={userFormErrors.email ? 'error' : ''}
                />
                {userFormErrors.email && (
                  <span className="error-message">{userFormErrors.email}</span>
                )}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>{t('userModal.phone')}</label>
                  <input
                    type="tel"
                    value={userForm.phone}
                    onChange={(e) => handleUserFormChange('phone', e.target.value)}
                    placeholder={t('userModal.phonePlaceholder')}
                  />
                </div>
                
                <div className="form-group">
                  <label>{t('userModal.role')}</label>
                  <select
                    value={userForm.role}
                    onChange={(e) => handleUserFormChange('role', e.target.value)}
                  >
                    <option value="student">{t('userModal.student')}</option>
                    <option value="teacher">{t('userModal.teacher')}</option>
                    <option value="parent">{t('userModal.parent')}</option>
                    <option value="school_admin">{t('userModal.schoolAdmin')}</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>{t('userModal.country')}</label>
                  <input
                    type="text"
                    value={userForm.country}
                    onChange={(e) => handleUserFormChange('country', e.target.value)}
                    placeholder={t('userModal.countryPlaceholder')}
                  />
                </div>
                
                <div className="form-group">
                  <label>{t('userModal.city')}</label>
                  <input
                    type="text"
                    value={userForm.city}
                    onChange={(e) => handleUserFormChange('city', e.target.value)}
                    placeholder={t('userModal.cityPlaceholder')}
                  />
                </div>
              </div>
              
              <div className="form-buttons">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowUserModal(false);
                    setUserForm({
                      first_name: '',
                      last_name: '',
                      email: '',
                      phone: '',
                      country: '',
                      city: '',
                      role: 'student'
                    });
                    setUserFormErrors({});
                  }}
                >
                  {t('userModal.cancel')}
                </button>
                <button 
                  type="submit" 
                  disabled={loading}
                >
                  {loading ? t('userModal.creating') : t('userModal.createUser')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;